/**
 * 토스트 알림 컴포넌트
 *
 * 개별 토스트 알림을 렌더링합니다. AppToastContainer에 의해 관리되며,
 * 성공, 실패, 경고, 정보 등 다양한 타입의 알림을 지원합니다.
 * 각 토스트는 우측에서 슬라이드 인 애니메이션으로 표시됩니다.
 *
 * @component
 * @example
 * // 토스트 컴포넌트는 일반적으로 직접 사용하지 않고,
 * // ToastContext와 useToast hook을 통해 사용합니다.
 *
 * const { toast } = useToast();
 * toast({ type: 'success', message: '저장되었습니다!' });
 *
 * @typedef {object} AppToastProps
 * @property {AppToast} toast - 토스트 데이터 객체
 * @property {'success' | 'error' | 'warning' | 'info'} toast.type - 토스트 타입
 * @property {string} toast.message - 표시할 메시지
 * @property {object} [toast.action] - 선택적 액션 버튼
 * @property {string} toast.action.label - 액션 버튼 텍스트
 * @property {() => void} toast.action.onClick - 액션 클릭 핸들러
 * @property {() => void} onClose - 토스트를 닫을 때 호출되는 콜백
 *
 * @returns {JSX.Element} 스타일이 적용된 토스트 알림 엘리먼트
 */

'use client';

import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { AppToast as ToastType } from '@/lib/stores/toastStore';

export interface AppToastProps {
  toast: ToastType;
  onClose: () => void;
}

export function AppToast({ toast, onClose }: AppToastProps) {
  const getBackground = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getIconColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      case 'warning':
        return 'text-yellow-600';
      case 'info':
      default:
        return 'text-blue-600';
    }
  };

  const getTextColor = () => {
    switch (toast.type) {
      case 'success':
        return 'text-green-900';
      case 'error':
        return 'text-red-900';
      case 'warning':
        return 'text-yellow-900';
      case 'info':
      default:
        return 'text-blue-900';
    }
  };

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-lg border shadow-lg pointer-events-auto transition-all duration-300 animate-slideIn ${getBackground()}`}
      role="alert"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 ${getIconColor()}`}>
        {getIcon()}
      </div>

      {/* Message */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${getTextColor()}`}>
          {toast.message}
        </p>
      </div>

      {/* Action AppButton (if provided) */}
      {toast.action && (
        <button
          onClick={() => {
            toast.action?.onClick();
            onClose();
          }}
          className={`flex-shrink-0 text-sm font-semibold underline hover:no-underline transition-colors ${getTextColor()}`}
        >
          {toast.action.label}
        </button>
      )}

      {/* Close AppButton */}
      <button
        onClick={onClose}
        className={`flex-shrink-0 hover:opacity-70 transition-opacity ${getIconColor()}`}
        aria-label="알림 닫기"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

// Tailwind animation
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .animate-slideIn {
      animation: slideIn 0.3s ease-out;
    }
  `;
  document.head.appendChild(style);
}
